import styles from './styles.module.scss';
import { useRestrictionsForm } from '@/hooks/restrictions/useRestrictionsForm';
import { useRestrictionById } from '@/api/restrictions/queries';
import { FormProvider, useForm } from 'react-hook-form';
import { useCallback } from 'react';
import { Restriction } from '@/models/restriction';
import { useCreateRestriction, useUpdateRestriction } from '@/api/restrictions';
import RestricitonsModalContentCoinSelector from './Coin';
import { RestrictionCategoryId } from '@/models/restriction/category/id';
import { CreateRestrictionRequestDto } from '@/services/dto/restrictions/create-restriction-input.dto';
import { UpdateRestrictionRequestDto } from '@/services/dto/restrictions/update-restriction-input.dto';
import {
  Button,
  ErrorCard,
  FormGroup,
  Input,
} from '@starsoft/common/components';
import useTranslation from 'next-translate/useTranslation';
import { RestrictionTypeId } from '@/models/restriction/type/id';
import RestricitonsModalContentStatus from './Status';
import RestricitonsModalContentFrameSelector from './Frame';
import { parseToBigInt } from '@starsoft/common/utils/parsers';
import { getMoneyOrAmount, parseToSubunits } from '@starsoft/common/utils';

export default function RestrictionModalContent() {
  const { t } = useTranslation('account');
  const { isCreating, id, restrictionCategory } = useRestrictionsForm();

  const methods = useForm<
    CreateRestrictionRequestDto & UpdateRestrictionRequestDto
  >({
    defaultValues: {
      typeId: RestrictionTypeId.User,
      categoryId: !!restrictionCategory
        ? (parseToBigInt(restrictionCategory) as bigint)
        : undefined,
    },
  });

  const { handleSubmit, reset, register, watch } = methods;
  const { createRestriction, loadingCreateRestriction } =
    useCreateRestriction();
  const { updateRestriction, loadingUpdateRestriction } =
    useUpdateRestriction();

  const categoryId = watch('categoryId');
  const timeFrameId = watch('frameId');

  const handleExternalOnSuccess = useCallback(
    (data: Restriction) => {
      reset({
        categoryId: data?.category?.id,
        coinId: data?.coin?.id,
        typeId: data?.type?.id,
        frameId: data?.frame?.id,
        duration: data?.duration,
        maxValue: getMoneyOrAmount(data?.maxValue),
        status: data?.status,
      });
    },
    [reset],
  );

  const { errorRestriction, isLoadingRestriction, refetchRestriction } =
    useRestrictionById({
      id: Number(id),
      externalOnSuccess: handleExternalOnSuccess,
    });

  if (errorRestriction) {
    return <ErrorCard error={errorRestriction} refetch={refetchRestriction} />;
  }

  function onSubmit(
    data: CreateRestrictionRequestDto & UpdateRestrictionRequestDto,
  ) {
    if (isCreating) {
      createRestriction({
        ...(data as CreateRestrictionRequestDto),
        maxValue: parseToSubunits(data?.maxValue, data?.coin),
        typeId: undefined,
        coin: undefined,
      });
      return;
    }

    updateRestriction({
      ...(data as UpdateRestrictionRequestDto),
      typeId: undefined,
      categoryId: undefined,
      maxValue: parseToSubunits(data?.maxValue, data?.coin),
      coin: undefined,
      id: parseToBigInt(id) as bigint,
    });
  }

  return (
    <FormProvider {...methods}>
      <form className={styles.container} onSubmit={handleSubmit(onSubmit)}>
        <div className={styles.container__content}>
          {!isCreating && <RestricitonsModalContentStatus />}
          {/* {isCreating && <RestricitonsModalContentCategorySelector />} */}
          <RestricitonsModalContentFrameSelector />
          <FormGroup
            label={t('restriction_duration_placeholder') as string}
            isLoading={isLoadingRestriction}
            description={
              timeFrameId
                ? (t('restriction_duration_description', {
                    timeFrame: t(`${timeFrameId}_frame_label_plural`),
                  }) as string)
                : undefined
            }
            loading={{ label: true, secondary: true }}
          >
            <Input
              register={register}
              name="duration"
              placeholder={t('restriction_duration_placeholder') as string}
            />
          </FormGroup>
          {[
            RestrictionCategoryId.Deposit?.toString(),
            RestrictionCategoryId.Withdrawal?.toString(),
            RestrictionCategoryId.Stake?.toString(),
            RestrictionCategoryId.Spend?.toString(),
          ].includes(categoryId?.toString()) && (
            <RestricitonsModalContentCoinSelector />
          )}

          {[
            RestrictionCategoryId.Deposit?.toString(),
            RestrictionCategoryId.Withdrawal?.toString(),
            RestrictionCategoryId.Stake?.toString(),
            RestrictionCategoryId.Spend?.toString(),
          ].includes(categoryId?.toString()) && (
            <FormGroup
              label={t('restriction_max_value_label') as string}
              isLoading={isLoadingRestriction}
              loading={{ label: true, secondary: true }}
            >
              <Input
                register={register}
                name="maxValue"
                placeholder={t('restriction_max_value_label') as string}
              />
            </FormGroup>
          )}
        </div>
        <Button
          isSkeleton={isLoadingRestriction}
          isloading={loadingCreateRestriction || loadingUpdateRestriction}
        >
          {t(isCreating ? 'create_restriction' : 'edit_restriction')}
        </Button>
      </form>
    </FormProvider>
  );
}
